'use strict';

const libPictProvider = require('pict-provider');

// The role index at which the server itself stops narrowing document reads to a user's own reach and
// opens them to the whole customer (Project.ts buildUserProjectMembershipJoinQuery). Matching it here
// means an administrator's program view comes from the same line the API already draws, rather than a
// second, drifting definition of "admin".
const PROGRAM_ROLE_INDEX = 4;

// Meadow filter-length safety: ids per INN chunk, and the page size each chunk is paged through.
const INN_CHUNK = 100;
const PAGE_SIZE = 2000;
// Dossier documents are the reach spine; this is the cap on that one read.
const DOSSIER_CAP = 4000;

/**
 * InspectionScopeService — answers "whose work is this?" once per session.
 *
 * Three questions, resolved at login and cached for the session:
 *
 *   1. WHICH TIER is this user — program (an administrator who should see everything) or scoped?
 *   2. WHICH INSPECTIONS can they reach?
 *   3. WHICH TEAMS are they on, and which do they lead?
 *
 * ── An honest warning about what this is ───────────────────────────────────────────────────────
 *
 * This is a LENS, not a permission. The Inspection endpoints do not scope per user: every non-admin
 * role reads Inspection with `MyCustomer`, which is a post-fetch per-record assertion that the
 * controller has already satisfied by injecting the session's IDCustomer into the query. There are no
 * query hooks on the Inspection bundle. So a scoped user who types a URL, or calls the REST API
 * directly, still sees every inspection in their customer.
 *
 * Narrowing what the app SHOWS is worth doing on its own — a field inspector opening a program of 825
 * inspections to find their twelve is a bad tool. But do not describe this as access control, and do
 * not let it be the reason a sensitive field is considered protected. Making it enforced is a change
 * to `api/headlight/ts_source/bundles/process/Inspections.ts`, not to this file.
 *
 * ── Ownership is not reach, and the difference is the whole design ─────────────────────────────
 *
 * There are two different answers to "their inspections", and conflating them produces a field tool
 * that still opens on hundreds of rows:
 *
 *   OWNERSHIP — what is MINE. `InspectionUserJoin` rows naming me, plus the assignments of teams I am
 *   on. This is what an inspector means by "my inspections", and it is what the app shows by default.
 *
 *   REACH — what I am ALLOWED to open. Derived from documents: `Documents/Lite` narrows rows to
 *   documents the caller reaches by project membership or by a role/team grant, so anything reached
 *   that way is reach the server already agrees with. It is much wider than ownership — on the live
 *   customer a lead inspector owns ~142 inspections but can reach every inspection in the 166
 *   projects he belongs to.
 *
 * The app defaults to OWNERSHIP and keeps REACH as the ceiling: it is what a "show everything I can
 * see" affordance would widen to, and what an enforcement change would narrow to. Never widen the
 * default view to reach — that is the difference between a tool that opens on your twelve bridges and
 * one that opens on eight hundred.
 *
 * ── Fail CLOSED ────────────────────────────────────────────────────────────────────────────────
 *
 * If resolution fails, a scoped user gets an EMPTY reach, not an unfiltered one. That is the opposite
 * of the sibling UserRoleMembershipFilter, which degrades open — and deliberately so: that filter
 * widens a list the user already had, while this one is the only thing keeping a field tool from
 * opening as a program tool. An empty screen is a visible, reportable bug; a silently unfiltered one
 * is not.
 */
class InspectionScopeService extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		/** Resolved once per session. Null until resolveAsync() completes. */
		this._scope = null;
		/** The in-flight resolution, so concurrent callers share one pass. */
		this._resolving = null;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	/**
	 * The signed-in user's id.
	 *
	 * The session packet spells this `UserID`, NOT `IDUser` — the entity column name and the session
	 * field name differ. Reading `IDUser` here yields undefined and builds `FBV~IDUser~EQ~undefined`,
	 * which returns nothing and looks exactly like "this user has no work".
	 *
	 * @return {Number}
	 */
	get idUser()
	{
		return Number(this.currentUser.UserID) || 0;
	}

	get idCustomer()
	{
		return Number(this.currentUser.CustomerID) || 0;
	}

	/**
	 * Is this session entitled to the whole program?
	 *
	 * Deliberately keyed on the session's role index rather than a capability. `canHash()` returns TRUE
	 * before its map has loaded, so gating a NARROWING on a capability would show everyone everything
	 * during the window this service runs in — the precise bug being fixed. A capability may WIDEN
	 * (see options.ProgramCapability), never narrow.
	 *
	 * @return {Boolean}
	 */
	isProgramTier()
	{
		if (Number(this.currentUser.UserRoleIndex) >= PROGRAM_ROLE_INDEX) { return true; }
		const tmpCapability = this.options.ProgramCapability;
		if (tmpCapability)
		{
			const tmpPermissions = this.fable.EffectivePermissionsService;
			if (tmpPermissions && (typeof tmpPermissions.currentUserReady === 'function') && tmpPermissions.currentUserReady())
			{
				return !!tmpPermissions.canHash(tmpCapability);
			}
		}
		return false;
	}

	/** The resolved scope, or null before resolveAsync(). @return {Object|null} */
	get scope() { return this._scope; }

	// --- REST helpers ---

	_get(pURL)
	{
		return new Promise((resolve) => this.restClient.getJSON(pURL, (pError, pBody) => resolve(pError ? null : pBody)));
	}

	/**
	 * Chunked, paged INN fetch. Paged to exhaustion on purpose: a truncated page here silently drops a
	 * user's own inspections, which is indistinguishable from not being assigned to them.
	 *
	 * @param {String} pEntity
	 * @param {String} pField
	 * @param {Array<Number>} pIDs
	 * @param {String} [pExtra] - extra filter clauses, leading with '~'
	 * @return {Promise<Array<Object>>}
	 */
	async _innFetch(pEntity, pField, pIDs, pExtra)
	{
		const tmpIDs = [ ...new Set(pIDs.map(Number).filter(Boolean)) ];
		const tmpOut = [];
		for (let i = 0; i < tmpIDs.length; i += INN_CHUNK)
		{
			const tmpChunk = tmpIDs.slice(i, i + INN_CHUNK);
			let tmpOffset = 0;
			for (;;)
			{
				const tmpRows = await this._get(`${ pEntity }/FilteredTo/FBL~${ pField }~INN~${ tmpChunk.join(',') }${ pExtra || '' }/${ tmpOffset }/${ PAGE_SIZE }`);
				const tmpArray = Array.isArray(tmpRows) ? tmpRows : [];
				tmpOut.push(...tmpArray);
				if (tmpArray.length < PAGE_SIZE) { break; }
				tmpOffset += PAGE_SIZE;
			}
		}
		return tmpOut;
	}

	// --- Resolution ---

	/**
	 * Resolve the session's scope. Safe to call repeatedly; the first call does the work.
	 *
	 * @param {Boolean} [pForce] - re-resolve even if already cached
	 * @return {Promise<Object>} the scope
	 */
	resolveAsync(pForce)
	{
		if (this._scope && !pForce) { return Promise.resolve(this._scope); }
		if (this._resolving && !pForce) { return this._resolving; }
		this._resolving = this._resolveAsync().then((pScope) =>
		{
			this._scope = pScope;
			this._resolving = null;
			return pScope;
		});
		return this._resolving;
	}

	async _resolveAsync()
	{
		const tmpScope =
		{
			Tier: 'scoped',
			IDUser: this.idUser,
			IDCustomer: this.idCustomer,
			// What is mine (personal + team assignment) — the default view.
			InspectionIDs: [],
			// What I may open (server-agreed document reach) — the ceiling, not the default.
			ReachIDs: [],
			Teams: [],
			LedTeamIDs: [],
			Ready: false,
			Degraded: false,
			Message: '',
		};

		if (!tmpScope.IDUser)
		{
			// No identity means no defensible scope. Fail closed and say why.
			tmpScope.Ready = true;
			tmpScope.Degraded = true;
			tmpScope.Message = 'The session did not report a user id, so this view could not be narrowed to your work.';
			return tmpScope;
		}

		if (this.isProgramTier())
		{
			tmpScope.Tier = 'program';
			tmpScope.Ready = true;
			// A program user still gets their teams resolved — "my team" is an identity question, not a
			// reach question, and an administrator may well lead a crew.
			await this._resolveTeamsAsync(tmpScope);
			this._log(tmpScope);
			return tmpScope;
		}

		try
		{
			// Teams first: a team's assignments are part of what is MINE, so they have to be resolved
			// before ownership is computed.
			await this._resolveTeamsAsync(tmpScope);

			const [ tmpReach, tmpMine, tmpTeamOwned ] = await Promise.all(
				[
					this._reachFromDocumentsAsync(),
					this._ownedByMeAsync(),
					this._ownedByMyTeamsAsync(tmpScope),
				]);
			tmpScope.ReachIDs = tmpReach;
			tmpScope.InspectionIDs = [ ...new Set([ ...tmpMine, ...tmpTeamOwned ]) ];
			tmpScope.OwnedByMe = tmpMine.length;
			tmpScope.OwnedByMyTeams = tmpTeamOwned.length;
			tmpScope.ReachFromDocuments = tmpReach.length;
			tmpScope.Ready = true;
		}
		catch (pError)
		{
			tmpScope.Ready = true;
			tmpScope.Degraded = true;
			tmpScope.InspectionIDs = [];
			tmpScope.Message = `Could not work out which inspections are yours (${ pError && pError.message ? pError.message : pError }).`;
		}

		this._log(tmpScope);
		return tmpScope;
	}

	/**
	 * Reach spine A — inspections whose dossier the server says this user can read.
	 *
	 * The sort stays on a Document column on purpose: sorting a gated read by a joined table's column
	 * drops the grant branch server-side, and a user who reaches their work ONLY through a team grant
	 * then sees nothing.
	 *
	 * @return {Promise<Array<Number>>}
	 */
	async _reachFromDocumentsAsync()
	{
		const tmpDocumentType = this.options.DossierDocumentType || 'Inspection-Dossier';
		const tmpRows = await this._get(`Documents/Lite/FilteredTo/FBV~IDCustomer~EQ~${ this.idCustomer }~FBV~DocumentType~EQ~${ encodeURIComponent(tmpDocumentType) }~FBV~Deleted~EQ~0~FSF~IDDocument~DESC~0/0/${ DOSSIER_CAP }`);
		const tmpDocumentIDs = (Array.isArray(tmpRows) ? tmpRows : []).map((pRow) => pRow.IDDocument).filter(Boolean);
		if (!tmpDocumentIDs.length) { return []; }
		const tmpJoins = await this._innFetch('InspectionDocumentJoins', 'IDDocument', tmpDocumentIDs, '~FBV~Deleted~EQ~0');
		return [ ...new Set(tmpJoins.map((pJoin) => Number(pJoin.IDInspection)).filter(Boolean)) ];
	}

	/**
	 * Ownership — inspections this user is personally named on.
	 *
	 * @return {Promise<Array<Number>>}
	 */
	async _ownedByMeAsync()
	{
		const tmpOut = [];
		let tmpOffset = 0;
		for (;;)
		{
			const tmpRows = await this._get(`InspectionUserJoins/FilteredTo/FBV~IDUser~EQ~${ this.idUser }~FBV~Deleted~EQ~0/${ tmpOffset }/${ PAGE_SIZE }`);
			const tmpArray = Array.isArray(tmpRows) ? tmpRows : [];
			tmpOut.push(...tmpArray.map((pRow) => Number(pRow.IDInspection)).filter(Boolean));
			if (tmpArray.length < PAGE_SIZE) { break; }
			tmpOffset += PAGE_SIZE;
		}
		return [ ...new Set(tmpOut) ];
	}

	/**
	 * Ownership — inspections assigned to any team this user is on.
	 *
	 * A crew shares its work, so a member should see the crew's inspections, not only the ones their
	 * own name happens to sit on. Resolved through the same InspectionUserJoin table as personal
	 * assignment: the team's roster, then every inspection any of them is named on.
	 *
	 * @param {Object} pScope - the scope being built; its Teams must already be resolved
	 * @return {Promise<Array<Number>>}
	 */
	async _ownedByMyTeamsAsync(pScope)
	{
		const tmpTeamIDs = (pScope.Teams || []).map((pTeam) => pTeam.IDTeam);
		if (!tmpTeamIDs.length) { return []; }
		const tmpMembers = await this._innFetch('TeamUserJoins', 'IDTeam', tmpTeamIDs, '~FBV~Deleted~EQ~0');
		const tmpMemberIDs = [ ...new Set(tmpMembers.map((pRow) => Number(pRow.IDUser)).filter(Boolean)) ];
		if (!tmpMemberIDs.length) { return []; }
		const tmpJoins = await this._innFetch('InspectionUserJoins', 'IDUser', tmpMemberIDs, '~FBV~Deleted~EQ~0');
		return [ ...new Set(tmpJoins.map((pRow) => Number(pRow.IDInspection)).filter(Boolean)) ];
	}

	/**
	 * Resolve the teams this user belongs to, and which of them they lead.
	 *
	 * Degrades quietly: a user without Team-Read gets no teams rather than a failed session. Team
	 * membership widens what the app OFFERS; it is not what keeps the reach honest.
	 *
	 * @param {Object} pScope - mutated in place
	 */
	async _resolveTeamsAsync(pScope)
	{
		const tmpMemberships = await this._get(`TeamUserJoins/FilteredTo/FBV~IDUser~EQ~${ this.idUser }~FBV~Deleted~EQ~0/0/200`);
		const tmpRows = Array.isArray(tmpMemberships) ? tmpMemberships : [];
		if (!tmpRows.length) { return; }

		const tmpTeamIDs = [ ...new Set(tmpRows.map((pRow) => Number(pRow.IDTeam)).filter(Boolean)) ];
		const tmpTeams = await this._innFetch('Teams', 'IDTeam', tmpTeamIDs, '~FBV~Deleted~EQ~0');
		const tmpNameByID = {};
		tmpTeams.forEach((pTeam) => { tmpNameByID[pTeam.IDTeam] = pTeam.Name || `Team ${ pTeam.IDTeam }`; });

		const tmpLeaderRoleIDs = await this._leaderRoleIDsAsync();
		pScope.Teams = tmpTeamIDs.map((pIDTeam) =>
		{
			const tmpRowsForTeam = tmpRows.filter((pRow) => (Number(pRow.IDTeam) === pIDTeam));
			// A user can hold several roles in one team — the membership is not 1:1 — so leadership is
			// "any of my rows in this team names a leader role".
			const tmpLeads = tmpRowsForTeam.some((pRow) => (tmpLeaderRoleIDs.indexOf(Number(pRow.IDRole)) > -1));
			return { IDTeam: pIDTeam, Name: tmpNameByID[pIDTeam] || `Team ${ pIDTeam }`, IDRoles: tmpRowsForTeam.map((pRow) => Number(pRow.IDRole)), Leads: tmpLeads };
		});
		pScope.LedTeamIDs = pScope.Teams.filter((pTeam) => pTeam.Leads).map((pTeam) => pTeam.IDTeam);
	}

	/**
	 * The IDRole values that mean "leads this team".
	 *
	 * Resolved by GUID rather than hardcoded, because the label role is a per-customer row and its id
	 * differs between environments. Configured ids are honored too, for a deployment that wants to pin
	 * them. Cached for the session.
	 *
	 * @return {Promise<Array<Number>>}
	 */
	async _leaderRoleIDsAsync()
	{
		if (this._leaderRoleIDs) { return this._leaderRoleIDs; }
		const tmpConfigured = (this.options.LeaderRoleIDs || []).map(Number).filter(Boolean);
		if (tmpConfigured.length) { this._leaderRoleIDs = tmpConfigured; return tmpConfigured; }

		const tmpGUID = this.options.LeaderRoleGUID;
		if (!tmpGUID) { this._leaderRoleIDs = []; return this._leaderRoleIDs; }
		const tmpRows = await this._get(`Roles/FilteredTo/FBV~GUIDRole~EQ~${ encodeURIComponent(tmpGUID.replace('{IDCustomer}', String(this.idCustomer))) }~FBV~Deleted~EQ~0/0/5`);
		this._leaderRoleIDs = (Array.isArray(tmpRows) ? tmpRows : []).map((pRow) => Number(pRow.IDRole)).filter(Boolean);
		return this._leaderRoleIDs;
	}

	// --- Consumption ---

	/**
	 * The Meadow filter clause that narrows a query to this session's inspections, or '' for a program
	 * user.
	 *
	 * An empty reach yields an impossible clause rather than no clause. Omitting it would turn "you
	 * have no inspections" into "here is the entire program", which is the one failure this service
	 * exists to prevent.
	 *
	 * @param {String} [pField] - the id column on the entity being filtered
	 * @return {String} a clause beginning with '~', or ''
	 */
	inspectionClause(pField)
	{
		const tmpField = pField || 'IDInspection';
		const tmpScope = this._scope;
		if (!tmpScope || (tmpScope.Tier === 'program')) { return ''; }
		if (!tmpScope.InspectionIDs.length) { return `~FBV~${ tmpField }~EQ~0`; }
		return `~FBL~${ tmpField }~INN~${ tmpScope.InspectionIDs.join(',') }`;
	}

	/**
	 * A short, stable signature of the current scope, for cache keys. A provider that caches rows under
	 * its filter signature must include this, or switching users serves the previous user's work.
	 *
	 * @return {String}
	 */
	signature()
	{
		const tmpScope = this._scope;
		if (!tmpScope) { return 'unresolved'; }
		if (tmpScope.Tier === 'program') { return 'program'; }
		return `u${ tmpScope.IDUser }:n${ tmpScope.InspectionIDs.length }`;
	}

	/** Whether a scoped user's view should carry a "this is only your work" note. @return {Boolean} */
	get isNarrowed()
	{
		return !!(this._scope && (this._scope.Tier !== 'program'));
	}

	_log(pScope)
	{
		this.pict.log.info(`InspectionScope: tier=${ pScope.Tier } user=${ pScope.IDUser } inspections=${ pScope.InspectionIDs.length }`
			+ ` (mine=${ pScope.OwnedByMe != null ? pScope.OwnedByMe : '-' } team=${ pScope.OwnedByMyTeams != null ? pScope.OwnedByMyTeams : '-' } reach=${ pScope.ReachFromDocuments != null ? pScope.ReachFromDocuments : '-' })`
			+ ` teams=${ pScope.Teams.length } leads=${ pScope.LedTeamIDs.length }${ pScope.Degraded ? ' DEGRADED' : '' }`);
	}
}

module.exports = InspectionScopeService;
module.exports.PROGRAM_ROLE_INDEX = PROGRAM_ROLE_INDEX;
module.exports.default_configuration =
{
	ProviderIdentifier: 'InspectionScope',
	AutoInitialize: true,
	AutoInitializeOrdinal: 25,

	// The document type whose grants stand in for "who works this inspection".
	DossierDocumentType: 'Inspection-Dossier',
	// IDRole values on TeamUserJoin that mean "leads this team". Leave empty to resolve by GUID below;
	// set explicitly to pin them for a deployment.
	LeaderRoleIDs: [],
	// The per-customer label role that marks a team lead. A Role row only because that is the column's
	// type — it carries no permissions and is never assigned through UserRoleJoin. Seeded by
	// tmp/HDOT-Team-Seed-Onetime/seed-inspection-teams.cjs.
	LeaderRoleGUID: 'SEED-ROLE-C{IDCustomer}-TEAMLEADER',
	// A capability that may WIDEN a user to the program view. Never used to narrow.
	ProgramCapability: '',
};
